import type { TrashedDocument } from "@models";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmationModal } from "src/components/ConfirmationModal";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import { ROUTES, ROUTE_SLUGS } from "src/config/navigation/routes";
import {
  useEmptyDocumentTrashMutation,
  usePermanentlyDeleteDocumentMutation,
  useRestoreDocumentMutation,
} from "src/hooks/mutations";
import { useGetTrashedDocuments } from "src/hooks/queries";
import { fillRouteWithSlugs } from "src/utils/navigation";
import { DocumentTrashTable } from "./DocumentTrashTable";
import { DocumentsPageViewToggle } from "./DocumentsPageViewToggle";

type DocumentTrashViewProps = {
  orgId: string;
};

export const DocumentTrashView = ({ orgId }: DocumentTrashViewProps) => {
  const navigate = useNavigate();
  const { data: documents = [], isLoading } = useGetTrashedDocuments(orgId);
  const restoreMutation = useRestoreDocumentMutation();
  const permanentDeleteMutation = usePermanentlyDeleteDocumentMutation();
  const emptyTrashMutation = useEmptyDocumentTrashMutation();
  const [documentToDelete, setDocumentToDelete] =
    useState<TrashedDocument | null>(null);
  const [isEmptyTrashConfirmationOpen, setIsEmptyTrashConfirmationOpen] =
    useState(false);

  const permanentlyDelete = () => {
    if (!documentToDelete) {
      return;
    }

    const docId = documentToDelete.id;
    setDocumentToDelete(null);
    permanentDeleteMutation.mutate({ orgId, docId });
  };

  const emptyTrash = () => {
    setIsEmptyTrashConfirmationOpen(false);
    emptyTrashMutation.mutate({ orgId });
  };

  const restoreDocument = (document: TrashedDocument) => {
    restoreMutation.mutate({ orgId, docId: document.id });
  };

  const openDocument = (document: TrashedDocument) => {
    navigate(
      fillRouteWithSlugs(ROUTES.TRASH_DOCUMENT, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
        [ROUTE_SLUGS.DOC_ID]: document.id,
      })
    );
  };

  const documentsRoute = fillRouteWithSlugs(ROUTES.DOCUMENTS, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
  });
  const isMutating =
    restoreMutation.isPending ||
    permanentDeleteMutation.isPending ||
    emptyTrashMutation.isPending;

  return (
    <>
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb
            items={[
              { label: "Documents", to: documentsRoute },
              { label: "Trash" },
            ]}
          />
        }
        rightButtons={<DocumentsPageViewToggle />}
        rightPanelToggleRequiresDevice={false}
      />
      <DocumentTrashTable
        documents={documents}
        isLoading={isLoading}
        isMutating={isMutating}
        onEmptyTrash={() => setIsEmptyTrashConfirmationOpen(true)}
        onOpenDocument={openDocument}
        onPermanentlyDelete={setDocumentToDelete}
        onRestore={restoreDocument}
      />

      <ConfirmationModal
        confirmText="Permanently delete"
        content={`“${documentToDelete?.title ?? "This document"}” and all of its revisions will be permanently deleted. This cannot be undone.`}
        onAbort={() => setDocumentToDelete(null)}
        onConfirm={permanentlyDelete}
        open={Boolean(documentToDelete)}
        title="Permanently delete document"
      />

      <ConfirmationModal
        confirmText="Empty Trash"
        content={`All ${documents.length} document${documents.length === 1 ? "" : "s"} in Trash will be permanently deleted, including their retained revisions and content. This cannot be undone.`}
        onAbort={() => setIsEmptyTrashConfirmationOpen(false)}
        onConfirm={emptyTrash}
        open={isEmptyTrashConfirmationOpen}
        title="Empty Trash"
      />
    </>
  );
};
